/**
 * agentParser.js — NLP extraction layer for the Onboarding Agent.
 *
 * Extracts structured entities from free-text user input:
 *   - city, state, country
 *   - sport names
 *   - organization type
 *   - numbers (athletes, coaches, etc)
 *   - person names, emails, phones
 *   - category references
 *   - module/service keywords
 *
 * No LLM required — rule-based with Brazilian Portuguese support.
 */

/* ── Normalize text for matching ── */
const norm = (s) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

/* ══════════════════════════════════════════════════════════
   DICTIONARIES
   ══════════════════════════════════════════════════════════ */

const SPORT_MAP = {
  natacao: 'swimming',  natação: 'swimming', swim: 'swimming', swimming: 'swimming',
  futebol: 'football',  soccer: 'football', football: 'football',
  basquete: 'basketball', basketball: 'basketball',
  volei: 'volleyball', vôlei: 'volleyball', volleyball: 'volleyball',
  atletismo: 'athletics', athletics: 'athletics',
  judo: 'judo', judô: 'judo',
  'polo aquatico': 'water_polo', 'water polo': 'water_polo',
  ginastica: 'gymnastics', ginástica: 'gymnastics',
};

const ORG_TYPE_MAP = {
  clube: 'club', club: 'club',
  academia: 'academy', escola: 'academy',
  equipe: 'team', time: 'team',
  federacao: 'federation', confederacao: 'federation', federação: 'federation',
  'projeto social': 'school',
};

const BRAZIL_STATES = {
  'acre': 'AC', 'alagoas': 'AL', 'amapa': 'AP', 'amazonas': 'AM',
  'bahia': 'BA', 'ceara': 'CE', 'distrito federal': 'DF', 'espirito santo': 'ES',
  'goias': 'GO', 'maranhao': 'MA', 'mato grosso': 'MT', 'mato grosso do sul': 'MS',
  'minas gerais': 'MG', 'minas': 'MG', 'para': 'PA', 'paraiba': 'PB',
  'parana': 'PR', 'pernambuco': 'PE', 'piaui': 'PI', 'rio de janeiro': 'RJ',
  'rio grande do norte': 'RN', 'rio grande do sul': 'RS', 'rondonia': 'RO',
  'roraima': 'RR', 'santa catarina': 'SC', 'sao paulo': 'SP', 'sergipe': 'SE',
  'tocantins': 'TO',
};

// Major Brazilian cities → state
const CITY_STATE = {
  'sao paulo': 'SP', 'rio de janeiro': 'RJ', 'belo horizonte': 'MG',
  'brasilia': 'DF', 'salvador': 'BA', 'fortaleza': 'CE', 'curitiba': 'PR',
  'recife': 'PE', 'porto alegre': 'RS', 'manaus': 'AM', 'belem': 'PA',
  'goiania': 'GO', 'guarulhos': 'SP', 'campinas': 'SP', 'santos': 'SP',
  'florianopolis': 'SC', 'vitoria': 'ES', 'natal': 'RN', 'cuiaba': 'MT',
  'campo grande': 'MS', 'teresina': 'PI', 'joao pessoa': 'PB',
  'sao luis': 'MA', 'maceio': 'AL', 'aracaju': 'SE', 'palmas': 'TO',
  'niteroi': 'RJ', 'ribeirao preto': 'SP', 'londrina': 'PR',
  'uberlandia': 'MG', 'contagem': 'MG', 'juiz de fora': 'MG',
  'joinville': 'SC', 'maringa': 'PR', 'sorocaba': 'SP', 'bauru': 'SP',
  'betim': 'MG', 'piracicaba': 'SP', 'jundiai': 'SP',
};

const CATEGORY_MAP = {
  mirim: 'mirim', 'pre-mirim': 'pre_mirim', 'pre mirim': 'pre_mirim',
  infantil: 'infantil', juvenil: 'juvenil', junior: 'junior', júnior: 'junior',
  adulto: 'adulto', senior: 'adulto', sênior: 'adulto',
  master: 'master', 'sub-10': 'mirim', 'sub-12': 'pre_mirim',
  'sub-14': 'infantil', 'sub-16': 'juvenil', 'sub-18': 'junior',
};

const MODULE_KEYWORDS = {
  resultado: ['results'], ranking: ['rankings'], evolucao: ['evolution'],
  benchmark: ['benchmarking'], dashboard: ['dashboard_tech', 'dashboard_phys'],
  treino: ['builder_tech', 'builder_phys'], periodizacao: ['builder_phys'],
  fisioterapia: ['physio'], fisio: ['physio'], nutricao: ['nutrition'],
  medicina: ['medicine'], reabilitacao: ['injury_recovery'],
  ia: ['ai_assistant', 'ai_training', 'ai_insights', 'ai_alerts'],
  inteligencia: ['ai_assistant', 'ai_insights'],
  video: ['video_upload', 'video_analysis'], analise: ['video_analysis', 'comparatives'],
  wearable: ['wearables'], sensor: ['sensors_env', 'sensor_integration'],
  relatorio: ['reports'], comparativo: ['comparatives'],
};

const ROLE_KEYWORDS = {
  treinador: 'TREINADOR', tecnico: 'TREINADOR', coach: 'TREINADOR',
  'preparador fisico': 'PREPARADOR_FISICO', 'prep fisico': 'PREPARADOR_FISICO',
  atleta: 'ATLETA', jogador: 'ATLETA', nadador: 'ATLETA',
  fisioterapeuta: 'FISIOTERAPEUTA', nutricionista: 'NUTRICIONISTA',
  medico: 'MEDICO', analista: 'ANALISTA', scout: 'OBSERVADOR',
  observador: 'OBSERVADOR', gestor: 'GESTOR', administrador: 'GESTOR',
};

/* ══════════════════════════════════════════════════════════
   EXTRACTION FUNCTIONS
   ══════════════════════════════════════════════════════════ */

export function extractSport(text) {
  const n = norm(text);
  for (const [keyword, value] of Object.entries(SPORT_MAP)) {
    if (n.includes(keyword)) return value;
  }
  return null;
}

export function extractOrgType(text) {
  const n = norm(text);
  for (const [keyword, value] of Object.entries(ORG_TYPE_MAP)) {
    if (n.includes(keyword)) return value;
  }
  return null;
}

export function extractCity(text) {
  const n = norm(text);
  for (const [city, state] of Object.entries(CITY_STATE)) {
    if (n.includes(city)) return { city: toTitleCase(city), state };
  }
  return null;
}

export function extractState(text) {
  const n = norm(text);
  // Check full name first
  for (const [name, uf] of Object.entries(BRAZIL_STATES)) {
    if (n.includes(name)) return uf;
  }
  // Check UF abbreviation (2 uppercase letters)
  const ufMatch = text.match(/\b([A-Z]{2})\b/);
  if (ufMatch && Object.values(BRAZIL_STATES).includes(ufMatch[1])) {
    return ufMatch[1];
  }
  return null;
}

export function extractNumbers(text) {
  const results = [];
  const patterns = [
    { regex: /(\d+)\s*(?:atletas?|nadador(?:es|a)?|jogador(?:es|a)?)/gi, label: 'athletes' },
    { regex: /(\d+)\s*(?:treinador(?:es|a)?|t[eé]cnico[s]?|coach(?:es)?)/gi, label: 'coaches' },
    { regex: /(\d+)\s*(?:categorias?)/gi, label: 'categories' },
    { regex: /(\d+)\s*(?:equipes?|times?|turmas?)/gi, label: 'teams' },
    { regex: /(\d+)\s*(?:modalidades?|esportes?)/gi, label: 'sports' },
    { regex: /(\d+)\s*(?:profission(?:ais|al)|staff|funcion[aá]rio[s]?)/gi, label: 'staff' },
  ];
  for (const { regex, label } of patterns) {
    let match;
    while ((match = regex.exec(text)) !== null) {
      results.push({ label, value: parseInt(match[1], 10) });
    }
  }
  return results;
}

export function extractEmail(text) {
  const match = text.match(/[\w.-]+@[\w.-]+\.\w{2,}/);
  return match ? match[0] : null;
}

export function extractPhone(text) {
  const match = text.match(/\(?(\d{2})\)?\s*(\d{4,5})[-.\s]?(\d{4})/);
  return match ? match[0].replace(/\D/g, '').replace(/^(\d{2})(\d{4,5})(\d{4})$/, '($1) $2-$3') : null;
}

export function extractCategories(text) {
  const n = norm(text);
  const found = [];
  for (const [keyword, value] of Object.entries(CATEGORY_MAP)) {
    if (n.includes(keyword) && !found.includes(value)) found.push(value);
  }
  return found;
}

export function extractModules(text) {
  const n = norm(text);
  const found = new Set();
  for (const [keyword, codes] of Object.entries(MODULE_KEYWORDS)) {
    if (n.includes(keyword)) codes.forEach((c) => found.add(c));
  }
  return [...found];
}

export function extractRoles(text) {
  const n = norm(text);
  const found = [];
  for (const [keyword, role] of Object.entries(ROLE_KEYWORDS)) {
    if (n.includes(keyword) && !found.includes(role)) found.push(role);
  }
  return found;
}

export function extractName(text) {
  // Simple heuristic: "Meu nome é X" or "me chamo X" or "sou o/a X"
  const patterns = [
    /(?:meu nome [eé]|me chamo|sou o |sou a |chamo[- ]?me)\s+([A-ZÀ-Ú][a-zà-ú]+(?:\s+[A-ZÀ-Ú][a-zà-ú]+)*)/i,
    /(?:nome:?\s*)([A-ZÀ-Ú][a-zà-ú]+(?:\s+[A-ZÀ-Ú][a-zà-ú]+)+)/i,
  ];
  for (const regex of patterns) {
    const match = text.match(regex);
    if (match) return match[1].trim();
  }
  return null;
}

/* ══════════════════════════════════════════════════════════
   MAIN PARSE FUNCTION
   ══════════════════════════════════════════════════════════ */

/**
 * Parse free-text and return all extracted entities.
 * @param {string} text - User's free-text input
 * @returns {Object} Extracted entities
 */
export function parseUserMessage(text) {
  if (!text || typeof text !== 'string') return { entities: {} };

  const entities = {};

  const sport = extractSport(text);
  if (sport) entities.sport = sport;

  const orgType = extractOrgType(text);
  if (orgType) entities.org_type = orgType;

  const cityData = extractCity(text);
  if (cityData) {
    entities.city = cityData.city;
    entities.state = cityData.state;
  }

  const state = extractState(text);
  if (state && !entities.state) entities.state = state;

  const numbers = extractNumbers(text);
  if (numbers.length) entities.numbers = numbers;

  const email = extractEmail(text);
  if (email) entities.email = email;

  const phone = extractPhone(text);
  if (phone) entities.phone = phone;

  const categories = extractCategories(text);
  if (categories.length) entities.categories = categories;

  const modules = extractModules(text);
  if (modules.length) entities.modules = modules;

  const roles = extractRoles(text);
  if (roles.length) entities.roles = roles;

  const name = extractName(text);
  if (name) entities.name = name;

  return { entities, raw: text };
}

/* ── Helpers ── */
function toTitleCase(s) {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}
